import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Show the newly selected colour straight away. The promise below does not
    // settle until the section fetch comes back, and until then the gallery
    // would carry on showing the previous colour's images.
    this.#showColourPreview(event.target);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return this.#restoreColourPreview();

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return this.#restoreColourPreview();

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        // An abort means a newer change is already in flight and will render.
        // Restoring here would flash the previous colour back first.
        if (error?.name === 'AbortError') return;

        this.#restoreColourPreview();
        console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Images swapped to a preview, with the attributes needed to put them back.
   * @type {{ img: HTMLImageElement, src: string, srcset: string | null, sizes: string | null }[]}
   */
  #previewedImages = [];

  /**
   * Grid items collapsed while a preview is showing.
   * @type {HTMLElement[]}
   */
  #previewHiddenItems = [];

  /**
   * Swaps the leading image over to the selected colour using a prefetched URL,
   * so the change is visible on click rather than after the network round trip.
   * Anything it changes is undone by the gallery being replaced wholesale when
   * the fetch resolves, or by #restoreColourPreview if that never happens.
   *
   * @param {Element} target - The element the product select event came from.
   */
  #showColourPreview(target) {
    const picker = target.closest('variant-picker');
    const selected = picker?.querySelector(
      'input[data-option-colour-code]:checked, option[data-option-colour-code]:checked'
    );
    const code = selected instanceof HTMLElement ? selected.dataset.optionColourCode : undefined;
    if (!code) return;

    const href = this.querySelector(`link[data-colour-preview="${CSS.escape(code)}"]`)?.getAttribute('href');
    if (!href) return;

    this.#restoreColourPreview();

    // The desktop grid and the mobile slideshow are separate lists over the same
    // media, so the leading image of each needs swapping.
    for (const selector of ['.media-gallery__grid > li', 'slideshow-slide']) {
      const [first, ...rest] = /** @type {HTMLElement[]} */ ([...this.querySelectorAll(selector)]);
      const img = first?.querySelector('img.product-media__image');

      if (img instanceof HTMLImageElement) {
        this.#previewedImages.push({
          img,
          src: img.src,
          srcset: img.getAttribute('srcset'),
          sizes: img.getAttribute('sizes'),
        });

        // srcset/sizes would otherwise keep winning over the new src.
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = href;
      }

      // Collapse the rest of the grid so the other colour's photos are not left
      // sitting alongside the new one. Slideshow slide visibility belongs to
      // slideshow.js, so those are left for the fetch to sort out.
      if (selector === '.media-gallery__grid > li') {
        for (const item of rest) {
          item.hidden = true;
          this.#previewHiddenItems.push(item);
        }
      }
    }
  }

  /** Puts the gallery back if the fetch never delivered a replacement. */
  #restoreColourPreview() {
    for (const { img, src, srcset, sizes } of this.#previewedImages) {
      img.src = src;
      if (srcset) img.setAttribute('srcset', srcset);
      if (sizes) img.setAttribute('sizes', sizes);
    }
    this.#previewedImages = [];

    for (const item of this.#previewHiddenItems) item.hidden = false;
    this.#previewHiddenItems = [];
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
